import { Modal, TextInput, Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useMutation } from '@redwoodjs/web'

import Button from 'src/components/Buttons/Button/Button'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_VOICE_AGENT,
  GET_VOICE_AGENTS,
} from 'src/graphql/voiceAgents.graphql'

interface AddVoiceAgentModalProps {
  opened: boolean
  close: () => void
}

// A VoE agent calls past employers to chase an employment verification. It is
// outbound by definition and registers no inbound numbers, so Direction and
// Phone Numbers drop away once it is selected.
const isInboundApplicant = (values: { type: string; direction: string }) =>
  values.type === 'APPLICANT' && values.direction === 'INBOUND'

const DEFAULT_NAMES: Record<string, string> = {
  APPLICANT: 'Jamie',
  VOE: 'VoE Agent',
}

const schema = z
  .object({
    type: z.enum(['APPLICANT', 'VOE']),
    direction: z.enum(['OUTBOUND', 'INBOUND']),
    phoneNumbers: z.string().optional(),
    voice: z.string().optional(),
    name: z.string().optional(),
  })
  .superRefine((val, ctx) => {
    if (isInboundApplicant(val) && !val.phoneNumbers?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['phoneNumbers'],
        message: 'Phone numbers are required',
      })
    }
  })

const AddVoiceAgentModal = ({ opened, close }: AddVoiceAgentModalProps) => {
  const form = useForm({
    initialValues: {
      type: 'APPLICANT',
      direction: 'OUTBOUND',
      phoneNumbers: '',
      voice: 'allie',
      name: 'Jamie',
    },
    validate: zodResolver(schema),
  })

  const isVoE = form.values.type === 'VOE'

  const [createVoiceAgent, { loading }] = useMutation(CREATE_VOICE_AGENT, {
    refetchQueries: [GET_VOICE_AGENTS],
    onCompleted: () => {
      toast('Voice agent added successfully!', 'success')
      form.reset()
      close()
    },
    onError: (error) => {
      toast(`Failed to create voice agent: ${error.message}`, 'error')
    },
  })

  const onSubmit = (values) => {
    const input = {
      type: values.type,
      direction: values.type === 'VOE' ? 'OUTBOUND' : values.direction,
      name: values.name,
      voice: values.voice,
      phoneNumbers: isInboundApplicant(values)
        ? values.phoneNumbers
            .split(',')
            .map((n) => n.trim())
            .filter(Boolean)
        : undefined,
    }
    createVoiceAgent({ variables: { input } })
  }

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      title="Add Voice Agent"
      size="40%"
    >
      <form onSubmit={form.onSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-6">
          <Select
            data={[
              { value: 'APPLICANT', label: 'Applicant' },
              { value: 'VOE', label: 'Verification of Employment' },
            ]}
            label="Type"
            description={
              isVoE
                ? 'Calls past employers to verify a driver’s employment history.'
                : 'Calls and screens driver applicants.'
            }
            withAsterisk
            {...form.getInputProps('type')}
            onChange={(value) => {
              const nextType = value || 'APPLICANT'
              // Only swap the name when it is still the other type's default,
              // so a name the admin typed survives switching back and forth.
              if (form.values.name === DEFAULT_NAMES[form.values.type]) {
                form.setFieldValue('name', DEFAULT_NAMES[nextType])
              }
              form.setFieldValue('type', nextType)
            }}
          />
          {!isVoE && (
            <Select
              data={[
                { value: 'OUTBOUND', label: 'Outbound' },
                { value: 'INBOUND', label: 'Inbound' },
              ]}
              label="Direction"
              withAsterisk
              {...form.getInputProps('direction')}
            />
          )}
          {isInboundApplicant(form.values) && (
            <TextInput
              label="Phone Numbers (comma separated)"
              withAsterisk
              {...form.getInputProps('phoneNumbers')}
            />
          )}
          <TextInput label="Name" {...form.getInputProps('name')} />
          <TextInput label="Voice" {...form.getInputProps('voice')} />
        </div>
        <div className="flex flex-row gap-3 border-t pt-4">
          <Button
            text="Cancel"
            variant="outline"
            className="flex-1"
            onClick={close}
          />
          <Button
            text="Save"
            className="flex-1"
            type="submit"
            loading={loading}
            disabled={!form.isValid()}
          />
        </div>
      </form>
    </Modal>
  )
}

export default AddVoiceAgentModal
